//
// File: pluginLabel.ts
// Description: Resolution of a plugin's display label for the viewer's locale
// Copyright: Broadlume 2025
//

import { useMemo } from 'react';
import { normalizeLocale, useCurrentLocale } from '../i18n/runtime';
import { PluginLabel, PluginLabelTranslationSettings } from './IShellPluginConfig';
import { PLUGIN_LABEL_FALLBACK_LOCALE, PLUGIN_LABEL_LOCALES } from './pluginLabelConfig';

/**
 * The shape every label resolution helper accepts: anything carrying a plugin
 * `name` and (optionally) a `configuration.label` block. Deliberately structural
 * rather than `IShellPluginConfig`, so the admin `IPlugin` row type and the API
 * summary types work without conversion.
 */
export interface PluginLabelSource {
  name: string;
  /**
   * Labels as sent on lightweight plugin summaries (the sidebar's payload),
   * where the full configuration is not included.
   */
  label?: PluginLabel | null;
  /**
   * Labels as stored on the full plugin record.
   */
  configuration?: {
    label?: PluginLabel;
    labelTranslation?: PluginLabelTranslationSettings;
  } | null;
}

/**
 * Labels can arrive either at the top level (lightweight plugin summaries, which
 * omit the configuration entirely) or nested under `configuration` (the full
 * plugin record). Prefer the summary field when present so both shapes resolve
 * identically.
 */
const getLabels = (plugin: PluginLabelSource): PluginLabel | undefined =>
  plugin.label ?? plugin.configuration?.label ?? undefined;

const readLabel = (label: PluginLabel | undefined, locale: string): string | undefined => {
  const value = label?.[locale];
  // Treat blank/whitespace-only labels as absent -- an admin clearing a field
  // should fall back, not render an empty menu item.
  return value?.trim() ? value.trim() : undefined;
};

/**
 * Resolve the label a plugin should display in a given locale.
 *
 * Resolution order:
 *   1. the requested locale (normalized, so 'es-MX' finds the 'es' label)
 *   2. the fallback locale (English)
 *   3. the plugin's `name`
 *
 * Step 3 is what keeps every plugin created before this feature working
 * unchanged: no label block means the name is displayed, exactly as today.
 */
export const resolvePluginLabel = (
  plugin: PluginLabelSource,
  locale: string,
  supportedLocales: readonly string[] = PLUGIN_LABEL_LOCALES,
  fallbackLocale: string = PLUGIN_LABEL_FALLBACK_LOCALE,
): string => {
  const label = getLabels(plugin);

  const normalized = normalizeLocale(locale, supportedLocales, fallbackLocale);

  return readLabel(label, normalized) ?? readLabel(label, fallbackLocale) ?? plugin.name;
};

/**
 * Hook form of {@link resolvePluginLabel}, bound to the viewer's current locale.
 * Use this at render sites (sidebar, launch menu, admin tables) instead of
 * printing `plugin.name` directly.
 */
export const usePluginLabel = (plugin: PluginLabelSource): string => {
  const locale = useCurrentLocale();

  return useMemo(() => resolvePluginLabel(plugin, locale), [plugin, locale]);
};

/**
 * Hook returning a resolver for the viewer's current locale, for callers that
 * need to label many plugins at once (tables, sort comparators, filter
 * predicates) where calling a hook per row is not possible.
 */
export const usePluginLabelResolver = (): ((plugin: PluginLabelSource) => string) => {
  const locale = useCurrentLocale();

  return useMemo(() => (plugin: PluginLabelSource) => resolvePluginLabel(plugin, locale), [locale]);
};

/**
 * Which supported locales a plugin has a usable label for. Backs the Language
 * Browser's coverage view and, in a later phase, tells the translation service
 * which locales still need filling.
 */
export const getPluginLabelCoverage = (
  plugin: PluginLabelSource,
  supportedLocales: readonly string[] = PLUGIN_LABEL_LOCALES,
): {
  locale: string;
  label?: string;
  isTranslated: boolean;
  /** Filled in by the translation service and not yet reviewed by a person. */
  isMachineTranslated: boolean;
}[] =>
  supportedLocales.map((locale) => {
    const label = readLabel(getLabels(plugin), locale);
    return {
      locale,
      label,
      isTranslated: Boolean(label),
      isMachineTranslated: Boolean(
        label && plugin.configuration?.labelTranslation?.machineTranslated?.[locale],
      ),
    };
  });

/**
 * Whether the plugin is set to keep its name untranslated (brand and product
 * names). Its other languages deliberately fall back to English, so they are
 * not "missing".
 */
export const isPluginLabelTranslationExcluded = (plugin: PluginLabelSource): boolean =>
  Boolean(plugin.configuration?.labelTranslation?.exclude);

/**
 * Whether the translation service would still translate this plugin's labels:
 * it has an English label, is not kept untranslated, and some other language is
 * blank or holds a machine translation of an older English label. Mirrors the
 * server's rules, so the admin UI can tell when translations are on their way.
 */
export const isPluginLabelTranslationPending = (
  plugin: PluginLabelSource,
  supportedLocales: readonly string[] = PLUGIN_LABEL_LOCALES,
  sourceLocale: string = PLUGIN_LABEL_FALLBACK_LOCALE,
): boolean => {
  if (isPluginLabelTranslationExcluded(plugin)) return false;

  const labels = getLabels(plugin);
  const english = readLabel(labels, sourceLocale);
  if (!english || english.length > 120) return false;

  const markers = plugin.configuration?.labelTranslation?.machineTranslated;
  return supportedLocales
    .filter((locale) => locale !== sourceLocale)
    .some((locale) => {
      if (!readLabel(labels, locale)) return true;
      const marker = markers?.[locale];
      return Boolean(marker && marker.sourceText !== english);
    });
};

/**
 * The supported locales a plugin is still missing a label for.
 */
export const getMissingPluginLabelLocales = (
  plugin: PluginLabelSource,
  supportedLocales: readonly string[] = PLUGIN_LABEL_LOCALES,
): string[] =>
  getPluginLabelCoverage(plugin, supportedLocales)
    .filter((entry) => !entry.isTranslated)
    .map((entry) => entry.locale);
